import { type Vec2 } from '../math';

import { type Evaluated, type MeasurementValue } from './evaluated';

/**
 * Whether a value is fit to leave the kernel.
 *
 * ## The contract this enforces
 *
 * ADR-0003 says a degenerate construction evaluates to `null` — undefined is a *value*, and
 * the editor draws nothing and recovers by itself when the user drags the geometry back into
 * a sensible place. `NaN` is the same statement made badly: it also means "there is no answer
 * here", but it says so in a form that spreads. It compares false against everything, so every
 * guard downstream passes it through; it propagates into every dependent construction; and it
 * surfaces as broken geometry a long way from whatever produced it.
 *
 * ## Why it lives at the boundary and not in each evaluator
 *
 * A systematic sweep of every construction against degenerate parents found three that leaked
 * `NaN` — circle and arc through three points, and an ellipse from its foci — all of them at
 * coordinates around 1e160, where a squared distance overflows to infinity and the division
 * that follows yields `NaN`. Fixing those three would have left the fourth, which is whichever
 * construction is written next.
 *
 * So the check is applied once, to whatever `evaluate` is about to return. That is the only
 * place with the property that no future evaluator can forget it.
 *
 * ## Why a switch rather than a walk over the object
 *
 * `evaluate` runs for every dirtied entity on every pointer move. A recursive walk over
 * `Object.values` allocates on that path; an exhaustive switch does not, and the compiler's
 * exhaustiveness check makes a new value kind a compile error here rather than a silent gap.
 */

const ok = (n: number): boolean => Number.isFinite(n);
const okPoint = (v: Vec2): boolean => ok(v.x) && ok(v.y);
const okPoints = (points: readonly Vec2[]): boolean => points.every(okPoint);

const okMeasurement = (measured: MeasurementValue): boolean =>
  measured.type === 'scalar' ? ok(measured.value) : okPoint(measured.value);

export function isFiniteValue(value: Evaluated): boolean {
  switch (value.kind) {
    case 'point':
      return okPoint(value.point);

    case 'segment':
      return okPoint(value.segment.a) && okPoint(value.segment.b);

    case 'ray':
      return okPoint(value.ray.origin) && okPoint(value.ray.direction);

    case 'line':
      return okPoint(value.line.origin) && okPoint(value.line.direction);

    case 'vector':
      return okPoint(value.from) && okPoint(value.to);

    case 'circle':
      return okPoint(value.circle.center) && ok(value.circle.radius);

    case 'arc':
      return (
        okPoint(value.arc.center) &&
        ok(value.arc.radius) &&
        ok(value.arc.startAngle) &&
        ok(value.arc.endAngle)
      );

    case 'ellipse':
      return (
        okPoint(value.ellipse.center) &&
        ok(value.ellipse.rx) &&
        ok(value.ellipse.ry) &&
        ok(value.ellipse.rotation)
      );

    case 'polygon':
      return okPoints(value.vertices);

    case 'polyline':
      return okPoints(value.points);

    case 'function-graph':
      return value.segments.every(okPoints);

    case 'axes':
      return (
        okPoint(value.origin) &&
        [value.xAxis, value.yAxis, ...value.ticks].every(
          (segment) => okPoint(segment.a) && okPoint(segment.b)
        ) &&
        value.marks.every((mark) => okPoint(mark.at))
      );

    case 'graph-marks':
      return (
        value.points.every(okPoint) &&
        value.rules.every((rule) => okPoint(rule.a) && okPoint(rule.b)) &&
        value.marks.every((mark) => okPoint(mark.at))
      );

    case 'variation-table':
      return (
        okPoint(value.corner) &&
        Number.isFinite(value.width) &&
        Number.isFinite(value.height) &&
        value.rules.every((rule) => okPoint(rule.a) && okPoint(rule.b)) &&
        value.marks.every((mark) => okPoint(mark.at))
      );

    case 'text':
      return okPoint(value.position);

    case 'measurement':
      // A measurement of degenerate geometry is exactly the case that yields a non-finite
      // number, and the measured quantity is a small union rather than a bare number.
      return okPoint(value.position) && okMeasurement(value.value);

    case 'image':
      return okPoint(value.position) && ok(value.width) && ok(value.height);

    case 'angle-mark':
      return (
        okPoint(value.vertex) &&
        okPoint(value.from) &&
        okPoint(value.to) &&
        ok(value.degrees) &&
        ok(value.armLength) &&
        ok(value.size)
      );

    // Four points, and no drawing to check. A frame with a non-finite axis would put every
    // computed point at NaN, so it is exactly as worth checking as a shape.
    case 'space':
      return (
        okPoint(value.frame.origin) &&
        okPoint(value.frame.x) &&
        okPoint(value.frame.y) &&
        okPoint(value.frame.z)
      );

    case 'solid':
      return (
        okPoint(value.origin) &&
        [...value.visible, ...value.hidden].every((edge) => okPoint(edge.a) && okPoint(edge.b))
      );
  }
}
